// dependencies
import * as Sentry from "@sentry/react";
import React from "react";
import { LoaderFunctionArgs } from "react-router-dom";

import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

//components
import { AdminContainer, AdminTitle } from "../../../components/Admin";
import Spacer from "../../../components/Spacer";

// hooks
import { useRbacPrimaryRolePermissions } from "../../../api/hooks";
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";

// queries
import {
	cohortQuery,
	cohortsQuery,
	companyEngagementSettingsQuery,
	companySettingsQuery,
	engagementSurveyQuestionsQuery,
	rbacPermissionsQuery,
} from "../../../api/queries";
import { SimplifiedQuery, dataLoader } from "../../../api/queries/dataLoader";

// css
import css from "./EngagementSettings.module.scss";
// Tabs
import CohortsTab from "./Tabs/Cohorts";
import CreateCohortTab from "./Tabs/CreateCohort";
import EditCohortTabEmployees from "./Tabs/EditCohort/Forms";
import EditCohortTabDetails from "./Tabs/EditCohort/Forms/Setup";
import ExcludedEmployees from "./Tabs/ExcludedEmployees";
import PulseSurveySettings from "./Tabs/PulseSurveySettings";
import SurveyQuestions from "./Tabs/SurveyQuestions";

interface BaseProps {
	tab:
		| "main"
		| "cohorts"
		| "create-cohort"
		| "cohort-employees"
		| "duplicate-cohort"
		| "edit-cohort-employees"
		| "edit-cohort-details"
		| "edit-cohort-employees-add-employees"
		| "questions"
		| "excluded-employees";
}

const componentLookup = {
	//main: <MainTab />,
	main: <PulseSurveySettings />,
	cohorts: <CohortsTab />,
	"create-cohort": <CreateCohortTab />,
	"duplicate-cohort": <CreateCohortTab />,
	"edit-cohort-details": <EditCohortTabDetails />,
	"edit-cohort-employees": <EditCohortTabEmployees view="employees" />,
	"edit-cohort-employees-add-employees": <EditCohortTabEmployees view="add-employees" />,
	questions: <SurveyQuestions />,
	"excluded-employees": <ExcludedEmployees />,
};

// Data loader function
export const engagementSettingsLoader = ({ params }: LoaderFunctionArgs) => {
	const queries: Array<() => SimplifiedQuery> = [
		() => rbacPermissionsQuery("engagement"),
		companySettingsQuery,
		cohortsQuery,
		engagementSurveyQuestionsQuery,
		companyEngagementSettingsQuery,
	];

	if (params.cohortID) {
		queries.push(() => cohortQuery(params.cohortID!));
	}

	return dataLoader([queries]);
};

const EngagementSettings = (props: BaseProps) => {
	const { tab } = props;
	const {
		value: ffEngagementSurveyEnabled,
		query: { isLoading: isLoadingEngagementSurveyFF },
	} = useFeatureFlagQuery("ff-is-engagement-survey-enabled", false);
	const { data: rbacPermissions, isLoading: isRbacLoading } = useRbacPrimaryRolePermissions("engagement");

	const isEngagementEnabled = ffEngagementSurveyEnabled;
	const isLoadingAny = isLoadingEngagementSurveyFF || isRbacLoading;

	return (
		<Sentry.ErrorBoundary fallback={undefined}>
			<AdminContainer>
				{!isEngagementEnabled ? (
					<>
						{isLoadingAny ? null : (
							<div>
								<AdminTitle includeSpacer={false}>Employee Engagement Settings</AdminTitle>

								<div className={css.infoBox}>
									<div className={css.infoIcon}>
										<FontAwesomeIcon icon={faInfoCircle} />
									</div>
									<div>This feature is not enabled for your organization.</div>
								</div>
							</div>
						)}
					</>
				) : (
					<>
						{isRbacLoading ? null : (
							<>
								{rbacPermissions?.includes("engagement:editSettings") ? (
									<>
										<Spacer height={4} />

										{componentLookup[tab] ?? null}
									</>
								) : (
									<div>
										<AdminTitle includeSpacer={false}>Employee Engagement Settings</AdminTitle>

										<div className={css.infoBox}>
											<div className={css.infoIcon}>
												<FontAwesomeIcon icon={faInfoCircle} />
											</div>
											<div>Missing required permissions.</div>
										</div>
									</div>
								)}
							</>
						)}
					</>
				)}
			</AdminContainer>
		</Sentry.ErrorBoundary>
	);
};

export default EngagementSettings;
